/**
 * @fileoverview Syndicator Showdown — Centralized Tournament Portfolio Context
 * @module context/EntryContext
 *
 * Single source of truth for a player's active stable across an entire slate.
 * All pricing calculations delegated to @ss/shared — never reimplemented here.
 */

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';

import {
  calculatePurchaseCost,
  calculatePerRaceFloor,
} from '@ss/shared';

import {
  MIN_RUNNERS_PER_RACE,
  MAX_FIELD_COVERAGE,
} from '@ss/shared/constants';

const EntryContext = createContext(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const EntryProvider = ({
  children,
  slateMetadata,
  racesMetadata = {},
  initialEntryData = null,
}) => {
  const {
    slateId,
    totalRaces,
    perRaceFloor: metadataFloor,
    entryLockMode,
    avgSpendPerRace,
  } = slateMetadata;

  const effectiveAvgSpend    = avgSpendPerRace || 100000;
  const totalStartingCapital = effectiveAvgSpend * totalRaces;

  // Correct parameter order: calculatePerRaceFloor(avgSpend, raceCount)
  const perRaceFloor = metadataFloor ?? calculatePerRaceFloor(effectiveAvgSpend, totalRaces);

  // ── Allocation state ────────────────────────────────────────────────────────
  const [allocations, setAllocations] = useState(
    () => initialEntryData?.allocations || {}
  );

  // ── Compliance state ────────────────────────────────────────────────────────
  const [complianceSummary, setComplianceSummary] = useState({});

  // ── Financial aggregation ───────────────────────────────────────────────────
  const financialAudit = useMemo(() => {
    let totalSpent = 0;
    const perRaceSpend = {};

    const safeAllocations = allocations && typeof allocations === 'object' ? allocations : {};

    Object.entries(safeAllocations).forEach(([raceId, raceAlloc]) => {
      const raceSpend = raceAlloc?.totalSpentOnRace || 0;
      totalSpent += raceSpend;
      perRaceSpend[raceId] = raceSpend;
    });

    let minimumStillRequired = 0;
    Object.entries(safeAllocations).forEach(([, raceAlloc]) => {
      const spent = raceAlloc?.totalSpentOnRace || 0;
      if (spent < perRaceFloor) {
        minimumStillRequired += perRaceFloor - spent;
      }
    });

    return {
      totalStartingCapital,
      totalSpent,
      remainingWallet:      Math.max(0, totalStartingCapital - totalSpent),
      minimumStillRequired,
      perRaceSpend,
    };
  }, [allocations, totalStartingCapital, perRaceFloor]);

  // ── Compliance checker ──────────────────────────────────────────────────────
  const checkCompliance = useCallback((nextAllocations) => {
    const summary = {};
    const safeNext = nextAllocations && typeof nextAllocations === 'object' ? nextAllocations : {};

    Object.entries(safeNext).forEach(([raceId, raceAlloc]) => {
      const activeRunnerCount = racesMetadata[raceId]?.activeRunnerCount || 1;
      const spent             = raceAlloc.totalSpentOnRace || 0;
      const runnersPicked     = raceAlloc.runnersPicked    || 0;
      const fieldCoverage     = runnersPicked / activeRunnerCount;

      const meetsFloor       = spent >= perRaceFloor;
      const meetsMinRunners  = runnersPicked >= MIN_RUNNERS_PER_RACE;
      const meetsFieldCap    = fieldCoverage <= MAX_FIELD_COVERAGE;

      summary[raceId] = {
        meetsFloor,
        meetsMinRunners,
        meetsFieldCap,
        fieldCoverage,
        runnersPicked,
        activeRunnerCount,
        totalSpentOnRace: spent,
        isCompliant:      meetsFloor && meetsMinRunners && meetsFieldCap,
        floorShortfall:   Math.max(0, perRaceFloor - spent),
      };
    });

    setComplianceSummary(summary);
  }, [racesMetadata, perRaceFloor]);

  /**
   * Single compliance trigger: fires after EVERY allocation state change.
   * Covers updateRunnerEquity, applyGeneratedStable, and clearRaceAllocation
   * uniformly — no setter can accidentally skip the compliance check.
   */
  useEffect(() => {
    if (Object.keys(allocations).length === 0) {
      setComplianceSummary({});
      return;
    }
    checkCompliance(allocations);
  }, [allocations, checkCompliance]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Updates a player's equity in one runner. Called by BuyModal on confirm.
   * equityPercent = 0 removes the runner from the allocation entirely.
   */
  const updateRunnerEquity = useCallback((raceId, runnerId, equityPercent, totalCost) => {
    setAllocations(prev => {
      const raceAlloc = prev[raceId] || { runners: {}, totalSpentOnRace: 0, runnersPicked: 0 };
      const runners   = { ...raceAlloc.runners };

      if (equityPercent > 0) {
        runners[runnerId] = { equityPercent, purchaseCost: totalCost };
      } else {
        delete runners[runnerId];
      }

      const totalSpentOnRace = Object.values(runners)
        .reduce((sum, r) => sum + (r?.purchaseCost || 0), 0);

      return {
        ...prev,
        [raceId]: {
          ...raceAlloc,
          runners,
          totalSpentOnRace,
          runnersPicked: Object.keys(runners).length,
        },
      };
    });
  }, []);

  /**
   * Replaces all allocations with a generated stable.
   */
  const applyGeneratedStable = useCallback((generatedAllocations) => {
    setAllocations(generatedAllocations);
  }, []);

  /**
   * Removes all runner selections for a single race.
   */
  const clearRaceAllocation = useCallback((raceId) => {
    setAllocations(prev => {
      const next = { ...prev };
      delete next[raceId];
      return next;
    });
  }, []);

  // ── Debounced Firestore write (placeholder) ─────────────────────────────────
  useEffect(() => {
    if (Object.keys(allocations).length === 0) return;
    const timeout = setTimeout(() => {
      // TODO: firestoreService.saveAllocation(slateId, allocations)
    }, 300);
    return () => clearTimeout(timeout);
  }, [allocations, slateId]);

  // ── Context value ───────────────────────────────────────────────────────────
  const value = useMemo(() => ({
    // Identifiers
    slateId,

    // State
    allocations,
    complianceSummary,
    financialAudit,

    // Config (for consumer convenience — avoids prop drilling)
    perRaceFloor,
    entryLockMode,
    totalRaces,              // Exposed so RaceCard uses correct race count for pricing
    totalStartingCapital,
    avgSpendPerRace: effectiveAvgSpend,

    // Actions
    updateRunnerEquity,
    applyGeneratedStable,
    clearRaceAllocation,
  }), [
    slateId,
    allocations,
    complianceSummary,
    financialAudit,
    perRaceFloor,
    entryLockMode,
    totalRaces,
    totalStartingCapital,
    effectiveAvgSpend,
    updateRunnerEquity,
    applyGeneratedStable,
    clearRaceAllocation,
  ]);

  return (
    <EntryContext.Provider value={value}>
      {children}
    </EntryContext.Provider>
  );
};

// ─── Consumer hook ─────────────────────────────────────────────────────────────

export const useEntryState = () => {
  const context = useContext(EntryContext);
  if (!context) {
    throw new Error('useEntryState must be used within an EntryProvider');
  }
  return context;
};